import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navbar from "@/components/Navbar";
import { 
  Search, 
  BookOpen, 
  Star,
  AlertTriangle,
  CheckCircle,
  Info,
  Filter,
  SortAsc,
  Beaker
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function IngredientReference() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all"); // all, safe, moderate, concern
  const [sortBy, setSortBy] = useState("name"); // name, rating, frequency

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients/reference`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIngredients(response.data);
    } catch (error) {
      console.error("Failed to load ingredients:", error);
      toast.error("Failed to load ingredient reference");
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort ingredients
  const filteredIngredients = ingredients
    .filter(ing => {
      // Search filter
      const matchesSearch = !searchQuery || 
        ing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ing.function?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Rating filter
      let matchesRating = true;
      if (ratingFilter === "safe") matchesRating = ing.rating >= 4;
      else if (ratingFilter === "moderate") matchesRating = ing.rating >= 3 && ing.rating < 4;
      else if (ratingFilter === "concern") matchesRating = ing.rating < 3;
      
      return matchesSearch && matchesRating;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sortBy === "frequency") return (b.product_count || 0) - (a.product_count || 0);
      return 0;
    });

  // Stats
  const stats = {
    total: ingredients.length,
    safe: ingredients.filter(i => i.rating >= 4).length,
    moderate: ingredients.filter(i => i.rating >= 3 && i.rating < 4).length,
    concern: ingredients.filter(i => i.rating && i.rating < 3).length,
    unrated: ingredients.filter(i => !i.rating).length
  };

  const getRatingInfo = (rating) => {
    if (!rating) return { color: "bg-gray-100 text-gray-600", label: "Unrated", icon: Info };
    if (rating >= 4) return { color: "bg-green-100 text-green-700", label: "Safe", icon: CheckCircle };
    if (rating >= 3) return { color: "bg-amber-100 text-amber-700", label: "Moderate", icon: Info };
    return { color: "bg-red-100 text-red-700", label: "Concern", icon: AlertTriangle };
  };

  const IngredientCard = ({ ingredient }) => {
    const ratingInfo = getRatingInfo(ingredient.rating);
    const RatingIcon = ratingInfo.icon;
    
    return (
      <Card className="glass hover:shadow-md transition-all" data-testid={`ingredient-${ingredient.name}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-medium text-primary">{ingredient.name}</h3>
                {ingredient.rating && (
                  <Badge className={`${ratingInfo.color} flex items-center gap-1`}>
                    <Star className="w-3 h-3" />
                    {ingredient.rating.toFixed(1)}
                  </Badge>
                )}
              </div>
              
              {ingredient.function && (
                <p className="text-sm text-muted-foreground mb-2">
                  <span className="font-medium">Function:</span> {ingredient.function}
                </p>
              )}
              
              {ingredient.description && (
                <p className="text-sm text-muted-foreground mb-3">
                  {ingredient.description}
                </p>
              )}
              
              <div className="flex flex-wrap gap-2">
                {ingredient.benefits?.slice(0, 2).map((benefit, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                    {benefit}
                  </Badge>
                ))}
                {ingredient.concerns?.slice(0, 1).map((concern, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                    ⚠️ {concern}
                  </Badge>
                ))}
              </div>
              
              {ingredient.product_count > 0 && (
                <p className="text-xs text-muted-foreground mt-3">
                  Found in {ingredient.product_count} of your products
                </p>
              )}
            </div>
            
            <div className={`p-2 rounded-lg ${ratingInfo.color}`}>
              <RatingIcon className="w-5 h-5" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-pulse-soft text-center">
            <Beaker className="w-16 h-16 mx-auto text-primary/30 mb-4" />
            <p className="text-muted-foreground">Loading ingredients...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="ingredient-reference">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 animate-fade-in-up">
          <div>
            <h1 className="font-serif text-3xl text-primary mb-2 flex items-center gap-3">
              <BookOpen className="w-8 h-8" />
              Ingredient Reference
            </h1>
            <p className="text-muted-foreground">
              {stats.total} unique ingredients across your products
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate("/products")}
            className="border-primary/30"
          >
            Back to Products
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-fade-in-up">
          <button
            onClick={() => setRatingFilter(ratingFilter === "safe" ? "all" : "safe")}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              ratingFilter === "safe" ? "border-green-500 bg-green-50" : "border-border bg-white/50 hover:border-green-300"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium">Safe</span>
            </div>
            <p className="text-xl font-bold text-green-600">{stats.safe}</p>
          </button>
          
          <button
            onClick={() => setRatingFilter(ratingFilter === "moderate" ? "all" : "moderate")}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              ratingFilter === "moderate" ? "border-amber-500 bg-amber-50" : "border-border bg-white/50 hover:border-amber-300"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium">Moderate</span>
            </div>
            <p className="text-xl font-bold text-amber-600">{stats.moderate}</p>
          </button>
          
          <button
            onClick={() => setRatingFilter(ratingFilter === "concern" ? "all" : "concern")}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              ratingFilter === "concern" ? "border-red-500 bg-red-50" : "border-border bg-white/50 hover:border-red-300"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium">Concern</span>
            </div>
            <p className="text-xl font-bold text-red-600">{stats.concern}</p>
          </button>
          
          <div className="p-4 rounded-xl border-2 border-border bg-white/50">
            <div className="flex items-center gap-2 mb-1">
              <Beaker className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium">Total</span>
            </div>
            <p className="text-xl font-bold text-primary">{stats.total}</p>
          </div>
        </div>

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6 animate-fade-in-up">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search ingredients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-skiniq"
              data-testid="ingredient-search"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={sortBy === "name" ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy("name")}
              className={sortBy === "name" ? "btn-primary" : ""}
            >
              <SortAsc className="w-4 h-4 mr-1" />
              A-Z
            </Button>
            <Button
              variant={sortBy === "rating" ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy("rating")}
              className={sortBy === "rating" ? "btn-primary" : ""}
            >
              <Star className="w-4 h-4 mr-1" />
              Rating
            </Button>
            <Button
              variant={sortBy === "frequency" ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy("frequency")}
              className={sortBy === "frequency" ? "btn-primary" : ""}
            >
              <Filter className="w-4 h-4 mr-1" />
              Frequency
            </Button>
          </div>
        </div>

        {/* Active Filters */}
        {(ratingFilter !== "all" || searchQuery) && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Filters:</span>
            {ratingFilter !== "all" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                {ratingFilter}
                <button onClick={() => setRatingFilter("all")} className="ml-1 hover:text-destructive">×</button>
              </Badge>
            )}
            {searchQuery && (
              <Badge variant="secondary" className="flex items-center gap-1">
                "{searchQuery}"
                <button onClick={() => setSearchQuery("")} className="ml-1 hover:text-destructive">×</button>
              </Badge>
            )}
            <span className="text-sm text-muted-foreground ml-2">
              Showing {filteredIngredients.length} ingredients
            </span>
          </div>
        )}

        {/* Ingredients List */}
        {filteredIngredients.length > 0 ? (
          <div className="grid gap-4 animate-fade-in-up">
            {filteredIngredients.map((ingredient, idx) => (
              <IngredientCard key={`${ingredient.name}-${idx}`} ingredient={ingredient} />
            ))}
          </div>
        ) : (
          <Card className="glass text-center py-16">
            <CardContent>
              <Beaker className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-serif text-xl text-primary mb-2">No Ingredients Found</h3>
              <p className="text-muted-foreground">
                {ingredients.length === 0 
                  ? "Add and analyze products to build your ingredient reference"
                  : "Try adjusting your search or filters"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* External Reference Link */}
        <div className="mt-8 p-4 bg-white/50 rounded-xl border border-border text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Want to learn more about cosmetic ingredients?
          </p>
          <a 
            href="https://incidecoder.com/ingredients" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline text-sm font-medium"
          >
            Visit INCIDecoder for detailed ingredient analysis →
          </a>
        </div>
      </main>
    </div>
  );
}
